/**
 * PreviewBanner — the header a shared caregiver/family page shows when it was
 * opened as a coordinator PREVIEW (a disposable `target="_blank"` tab), in place
 * of the normal ClientBreadcrumb / back-nav.
 *
 * WHY THIS EXISTS
 * `/clients/:id` (MeetPage) and `/clients/:id/playthrough` (PlayThroughPlayer)
 * are shared routes: real caregivers/family use them in their own single-tab
 * session (where the breadcrumb + "Coordinator →" nav is correct), AND the
 * coordinator opens them in a new tab to preview. In the preview tab, in-app
 * back-nav is actively confusing — "Your clients" implies navigating THIS
 * throwaway tab back into a list, and the coordinator's real context is the
 * OTHER tab. A preview tab is a leaf: no breadcrumb, no in-app back, its only
 * exit is "close the tab." The browser's own Back/Forward still work within the
 * tab (forward clicks push real history), so this banner deliberately offers no
 * in-app navigation — only a "Close preview" that closes the tab.
 *
 * DETECTION (in the host page, not here): read `?preview=1` (accept any truthy
 * value, incl. the legacy `?preview=true`). The opener adds it; role is NOT used
 * to detect preview, because a coordinator may legitimately open these pages
 * in-app for real — the param marks THIS tab as disposable.
 *
 * PROPS
 *   label — what the tab is previewing, e.g. "how this looks to a DSP".
 *           Rendered as: "Preview — {label}".
 *
 * CLOSE BEHAVIOR
 * window.close() reliably closes tabs opened via target="_blank"/window.open.
 * If the tab wasn't script-opened (e.g. the coordinator hand-edited the URL, or
 * duplicated the tab), window.close() may be a no-op; we show a fallback hint so
 * the coordinator isn't left stranded with a dead button.
 *
 * [forcing commit with a dummy line]
 */

import React, { useState } from "react";

export default function PreviewBanner({ label }) {
  const [closeFailed, setCloseFailed] = useState(false);

  function handleClose() {
    window.close();
    // If the tab is still here a tick later, close() was a no-op (tab wasn't
    // script-opened). Surface a fallback instead of a silently dead button.
    setTimeout(() => setCloseFailed(true), 150);
  }

  return (
    <div style={s.bar} role="status" aria-label="Preview mode">
      <div style={s.left}>
        <span style={s.badge}>Preview</span>
        <span style={s.label}>{label}</span>
      </div>
      <div style={s.right}>
        {closeFailed && (
          <span style={s.hint}>Close this tab to return to your work.</span>
        )}
        <button type="button" style={s.closeBtn} onClick={handleClose}>
          Close preview
        </button>
      </div>
    </div>
  );
}

const s = {
  bar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--space-3)",
    padding: "10px 24px",
    background: "var(--color-surface-alt, var(--color-surface))",
    borderBottom: "1px solid var(--color-border)",
    // A subtle top accent so the preview chrome reads as distinct from the real
    // caregiver header, reinforcing "this is a preview, not the live page."
    boxShadow: "inset 0 3px 0 var(--color-accent, var(--color-primary))",
  },
  left:  { display: "flex", alignItems: "center", gap: "var(--space-2)", minWidth: 0 },
  badge: {
    fontSize: "var(--text-xs)",
    fontWeight: "var(--weight-semibold, 600)",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: "var(--color-accent-text, var(--color-primary))",
    background: "var(--color-accent-subtle, transparent)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-sm, 4px)",
    padding: "2px 8px",
    whiteSpace: "nowrap",
  },
  label: {
    fontSize: "var(--text-sm)",
    color: "var(--color-text-secondary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  right: { display: "flex", alignItems: "center", gap: "var(--space-3)", flexShrink: 0 },
  hint:  { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  closeBtn: {
    fontSize: "var(--text-sm)",
    fontWeight: "var(--weight-medium)",
    color: "var(--color-text-primary)",
    background: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderRadius: "var(--radius-md, 6px)",
    padding: "6px 14px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
};
